import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";

import ICompAnalysis from "../../../types/ICompAnalysis";
import { formatDate } from "../../../utils/neo4jHelper";
import CloseIcon from "../../../components/CloseIcon";
import { useNitroStore } from "../../../store/Store";
import RateRequestForm from "./RouteValidation/requests/rateRequest";
import { IPinExtension } from "../../../types/ILocation";

const FreightDetails = ({
  record,
  close,
}: {
  record: ICompAnalysis;
  close?: () => void;
}) => {
  const auth = useAuth();
  const [mode, selectedLocation, product] = useNitroStore((state) => [
    state.mode,
    state.selectedLocation,
    state.product,
  ]);

  const [freightIsValid, setFreightIsValid] = useState(true);
  const [freighHasInvalid, setFreightHasInvalid] = useState(false);
  useEffect(() => {
    if (record.legs.length === 0) {
      return setFreightIsValid(false);
    }

    let newFreightIsValid = false;
    // Remove the logic because of bug 219559
    const newFreightHasInvalid = false;
    record.legs.forEach((leg) => {
      if (leg.dist && leg.dist > 0) {
        newFreightIsValid = true;
      }
    });

    setFreightIsValid(newFreightIsValid);
    setFreightHasInvalid(newFreightHasInvalid);
  }, [record]);

  const [rateRequestLocation, setRateRequestLocation] =
    useState<IPinExtension | null>(null);

  return (
    <>
      {rateRequestLocation && (
        <RateRequestForm
          currentRate={record.freight}
          marketer={`${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`}
          selectedLocation={rateRequestLocation}
          originCity={record.origin.name}
          product={product}
          close={() => {
            setRateRequestLocation(null);
          }}
          rateRequestType={record.isValid ? "Challenge" : "Verify"}
        />
      )}
      <div
        style={{
          padding: "8px 4px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          position: "relative",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 500, paddingLeft: 10 }}>
          Freight details
        </div>

        {close && (
          <div style={{ position: "absolute", top: 0, right: 8 }}>
            <CloseIcon
              onClick={() => {
                if (close) {
                  close();
                }
              }}
            />
          </div>
        )}

        <div style={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="freightDetailsHeader">Miles</TableCell>
                {record.mode === "TRUCK" && (
                  <TableCell className="freightDetailsHeader">$/mi</TableCell>
                )}
                {record.mode === "RAIL" && (
                  <TableCell className="freightDetailsHeader">
                    Min rail cars
                  </TableCell>
                )}

                <TableCell className="freightDetailsHeader">Rate</TableCell>
                <TableCell className="freightDetailsHeader">Fuel</TableCell>

                {record.mode === "TRUCK" && (
                  <TableCell className="freightDetailsHeader">FSC</TableCell>
                )}
                {record.mode === "RAIL" && (
                  <TableCell className="freightDetailsHeader">
                    Car owner
                  </TableCell>
                )}

                {record.mode === "RAIL" && (
                  <TableCell className="freightDetailsHeader">Route</TableCell>
                )}
                {record.mode === "RAIL" && (
                  <TableCell className="freightDetailsHeader">
                    Expiration date
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {record.legs.map((leg) => (
                <TableRow key={`${leg.dist}${leg.curr}`}>
                  {/* Miles */}
                  <TableCell className="freightDetailsCell">
                    {`${Math.round((leg.dist || 0) * 100) / 100}`}
                  </TableCell>
                  {/* Cost per Mile */}
                  {record.mode === "TRUCK" && (
                    <TableCell className="freightDetailsCell">
                      {`$${leg.costPerMile}`}
                    </TableCell>
                  )}
                  {/* Min rail cars */}
                  {record.mode === "RAIL" && (
                    <TableCell className="freightDetailsCell">
                      {leg.minCars}
                    </TableCell>
                  )}
                  {/* Rates */}
                  <TableCell className="freightDetailsCell">
                    {`$${Math.round(leg.rate * 100) / 100} / ${leg.rateUom}`}
                  </TableCell>
                  {/* Fuel */}
                  <TableCell className="freightDetailsCell">
                    {`${Math.round(Number(leg.fscRate) * 100) / 100}${
                      leg.fscRateUom
                    }`}
                  </TableCell>
                  {/* FSC */}
                  {record.mode === "TRUCK" && (
                    <TableCell className="freightDetailsCell">
                      {`$${Math.round(leg.fsc * 100) / 100} `}
                    </TableCell>
                  )}
                  {/* Car owner */}
                  {record.mode === "RAIL" && (
                    <TableCell className="freightDetailsCell">
                      {leg.carOwner || "Unknown"}
                    </TableCell>
                  )}
                  {/* Route */}
                  {record.mode === "RAIL" && (
                    <TableCell className="freightDetailsCell">
                      {leg.route || "Unknown"}
                    </TableCell>
                  )}
                  {/* Expiration date */}
                  {record.mode === "RAIL" && (
                    <TableCell className="freightDetailsCell">
                      {formatDate(leg.exp)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {mode === "RAIL" && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            {freighHasInvalid && (
              <Tooltip title="Report invalid miles in freight details">
                <Button
                  id="reportIssueButton"
                  variant="contained"
                  color="error"
                  onClick={() => {
                    if (!selectedLocation) {
                      // return;
                    }
                    // setReviewRequestLocation(selectedLocation);
                  }}
                >
                  Report Issue
                </Button>
              </Tooltip>
            )}
            <Tooltip
              title={
                freightIsValid
                  ? "Send an email to verify this rate matches OTM."
                  : "Freight details are invalid and missing miles. Report issue to have the mileage fixed before requesting new rates."
              }
            >
              <div>
                <Button
                  id="challengeOrVerifyRateButton"
                  disabled={freighHasInvalid}
                  variant="outlined"
                  sx={{
                    backgroundColor: "#FFDCDC",
                    borderColor: "#ff0000",
                    color: "black",
                  }}
                  color="error"
                  onClick={() => {
                    if (!selectedLocation) {
                      return;
                    }
                    setRateRequestLocation(selectedLocation);
                  }}
                >
                  {record.isValid ? "Challenge Rate" : "Verify Rate"}
                </Button>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );
};

export default FreightDetails;
